// Birim varyantı (spec 2026-10-30 §14 DR1–DR10) — gösterge birimi seçici + etki bandı kopyaları.
// TEK kaynak; `KPI_COPY.unit` olarak da açılır (kpi-ui.ts 600 satırı aştığı için ayrı dosya).
// Sayı biçimi burada değil (kpi-unit-options.ts `formatUnitNumber`); fonksiyonlar biçimlenmiş
// metin alır. Müşteri (portal) kopyası `KPI_COPY.portal.unitChanged` — "AI/üretim/danışman notu" yok.

const plural = (n: number, word: string): string => `${n.toString()} ${word}`;
/** "N değer, M hedef" — sıfır parçalar düşer; ikisi de 0 ise boş */
const countList = (values: number, targets: number, sep: string): string =>
  [values > 0 ? plural(values, "değer") : null, targets > 0 ? plural(targets, "hedef") : null]
    .filter(Boolean)
    .join(sep);

/** TODO-KPI-100: kod yeniden adlandırma onayı — formül sayılarının yanında birim şablonu sayısı */
export function renameConfirmBody(r: {
  from: string;
  to: string;
  catalogFormulas: number;
  sets: number;
  setFormulas: number;
  unitTemplates?: number | undefined;
  samples: readonly string[];
}): string {
  const templates =
    (r.unitTemplates ?? 0) > 0 ? ` ${plural(r.unitTemplates ?? 0, "birim şablonu")}` : "";
  const example = r.samples.length > 0 ? ` Örnek: ${r.samples.slice(0, 3).join(" · ")}` : "";
  return (
    `${r.from} → ${r.to}. Bu koda atıf yapan ${r.catalogFormulas.toString()} katalog formülü` +
    `${templates ? `,${templates}` : ""} ve ${r.sets.toString()} veri setindeki ` +
    `${r.setFormulas.toString()} formül varyantı otomatik güncellenecek ` +
    `(değerler, kanıtlar ve üretilmiş belgeler etkilenmez).${example}`
  );
}

export const KPI_UNIT_COPY = {
  fieldLabel: "Birim — müşteri genelinde",
  scopeLine: (client: string) => `${client} müşterisinin tüm veri setlerinde geçerli`,
  groups: {
    default: "Katalog varsayılanı",
    variants: "Tanımlı alternatifler",
    convertible: (cur: string) => `Mevcut birimden çevrilebilir (${cur})`,
    other: "Diğer birim…",
  },
  /** seçenek ipucu: mevcut etkin birim */
  currentHint: "mevcut",
  unitless: "Birimsiz",
  otherInputLabel: "Birim adı (en çok 32 karakter)",
  otherNote: "çevrim ve uyum denetimi yapılmaz",
  noCurrency: "otomatik kur çevrimi yok",
  derivedReadonly: (u: string) => `${u} — girdilerden hesaplanır`,
  derivedTemplate: "Birim şablonu",
  /** DR1 — IndicatorModal açıklaması: set alanları ↔ müşteri geneli birim */
  modalScope: "Etiket/konsolide/formül yalnız bu veri setine özel; birim müşteri genelinde",
  /** 409 yarış (DR4): sunucu iletisi + "Yeniden kontrol et" */
  conflict: (message: string) => `Birim değiştirilemedi — ${message}`,
  applyFailed: "Birim kaydedilemedi",
  band: {
    loading: "Etki hesaplanıyor…",
    error: "Etki hesaplanamadı — yeniden dene",
    retry: "Yeniden dene",
    none: "Değer/hedef yok; birim doğrudan değişir",
    /** (1) kapsam satırı: müşteri + mevcut → yeni + etkilenen veri seti */
    scope: (client: string, from: string, to: string, sets: number) =>
      `${client} · ${from} → ${to}` +
      (sets > 0 ? ` · ${plural(sets, "veri setinde")} geçerli` : ""),
    /** (2) sayımlar — örtüşmeyen: raporlanan, değerin alt kümesi */
    counts: (values: number, reported: number, targets: number) =>
      [
        values > 0 || reported === 0
          ? `${plural(values, "değer")}${reported > 0 ? ` (${reported.toString()} raporlanan)` : ""}`
          : `${plural(reported, "raporlanan değer")}`,
        targets > 0 ? `${plural(targets, "hedef/başlangıç")}` : null,
      ]
        .filter(Boolean)
        .join(" · "),
    span: (years: string | null, entities: number) =>
      [years, entities > 0 ? plural(entities, "işletme") : null].filter(Boolean).join(" · "),
    /** (3) DR8 — sayılar hangi birimde girildi (zorunlu) */
    radioLegend: "Sayılar hangi birimde girildi?",
    radioConvert: (old: string, k: string, sample: string | null) =>
      `Sayılar ${old} olarak girildi → ${k} çevir` + (sample ? ` (örn. ${sample})` : ""),
    radioRelabel: (newU: string, sample: string | null) =>
      `Sayılar zaten ${newU} → yalnız birim etiketi düzelsin` +
      (sample ? ` (örn. ${sample}; değer değişmez)` : " (değerler değişmez)"),
    radioRequired: "Kaydetmeden önce bir seçenek işaretleyin.",
    /** (4) DR6 — "geri alınabilir" ifadesi YOK */
    lossy: (count: number, sample: string | null) =>
      `${plural(count, "değer")} yuvarlanacak` +
      (sample ? ` — örn. ${sample}` : "") +
      " · eski değerler Sürümler'de görünür",
    lossySample: (raw: string, rounded: string, exact: string, pct: string) =>
      `${raw} → ${rounded} (tam: ${exact}; fark %${pct})`,
    /** (5) bilgi */
    dependentFormulas: (n: number) =>
      `Bu göstergeye bağlı ${plural(n, "formül")} yeni birimle hesaplanır`,
    noCorrectionBadge: "Müşteri ekranında “danışman düzeltti” rozeti çıkmaz.",
    /** DR5 — engelli durum */
    blockedTitle: "Birim değiştirilemiyor",
    blockedPaths: "ayrı gösterge açın ya da yeni birimde yeniden aktarın (İçe Aktar)",
    recheck: "Yeniden kontrol et",
    revert: "Birimi eski haline döndürün",
    cols: { indicator: "Gösterge", blocker: "Engel", affected: "Etkilenen", action: "İşlem" },
    kinds: {
      notConvertible: (from: string, to: string) => `${from} → ${to} çevrilemez`,
      formulaConflict: (from: string) => `Formül eski birimi (${from}) açıkça çeviriyor`,
      dependent: "Bu birimden türeyen hesaplanan gösterge",
    },
    affectedFormula: (setName: string | null) => (setName ? `1 formül · ${setName}` : "1 formül"),
    affectedDependent: (reported: number, targets: number) =>
      [
        reported > 0 ? plural(reported, "raporlanan") : null,
        targets > 0 ? plural(targets, "hedef") : null,
      ]
        .filter(Boolean)
        .join(" · "),
    actions: {
      openFormula: "Formülü aç",
      inspectValues: "Değerleri incele",
      inspectReported: "Raporlananları incele",
      inspectTargets: "Hedefleri incele",
    },
  },
  /** n=0 (yeniden etiketle / kullanım yok): sayfadaki Kaydet ile karışmasın diye "Birimi kaydet" */
  save: (n: number) => (n > 0 ? `Kaydet ve ${n.toString()} değeri çevir` : "Birimi kaydet"),
  saving: "Çevriliyor…",
  confirmTitle: "Birim değişikliğini onaylayın",
  confirm: (values: number, targets: number, unit: string) =>
    `${countList(values, targets, " ve ")} ${unit} birimine çevrilsin mi?`,
  confirmRelabel: (values: number, targets: number, unit: string) =>
    `${countList(values, targets, " ve ")} için birim ${unit} olarak düzeltilsin mi? Sayılar değişmez.`,
  /** DR6 başarı bandı — sıfır olan sayım parçası yazılmaz (confirm gibi) */
  success: (label: string, client: string, unit: string, values: number, targets: number) =>
    [
      `${label} birimi ${client} için ${unit} oldu`,
      countList(values, targets, ", ") ? `${countList(values, targets, ", ")} çevrildi` : null,
      "Sürümler'e yazıldı",
    ]
      .filter(Boolean)
      .join(" · "),
  /** DR8 "yalnız etiket düzelsin" yolu — sayılar değişmedi, "çevrildi" denmez */
  successRelabel: (label: string, client: string, unit: string, values: number, targets: number) =>
    [
      `${label} birimi ${client} için ${unit} oldu`,
      countList(values, targets, " ve ")
        ? `${countList(values, targets, " ve ")}in birimi düzeltildi (sayılar aynı)`
        : null,
      "Sürümler'e yazıldı",
    ]
      .filter(Boolean)
      .join(" · "),
  catalog: {
    defaultLabel: "Hangi birimle ölçülür?",
    defaultHelp: "Yeni müşteri setlerinde başlangıç birimi; mevcut müşteri kayıtları değişmez",
    defaultPlaceholder: "Birim seçin ya da yazın",
    derivedHelp: "{kod} girdinin birimini alır, örn. {ciro}/kişi → TL/kişi",
    usageLoading: "Kullanım denetleniyor…",
    inUse: (n: number) => `Bu gösterge ${plural(n, "müşteride")} kullanımda`,
    note: "Katalog varsayılanı yalnız kullanımı olmayan müşterilerde değişir",
    open: "Veri Yönetimi'nde aç",
    cols: { client: "Müşteri", usage: "Değer / hedef", unit: "Mevcut birim" },
    usage: (values: number, targets: number) =>
      `${plural(values, "değer")} · ${plural(targets, "hedef")}`,
  },
  /** DR9/DR13 — portal grid'inde kapalı hücrenin amber noktası (müşteri yüzeyi) */
  portalDot: "Birim değişti — değer kaydedilmedi",
  /** D20 — danışman yazımı 409 KPI_UNIT_CHANGED (hücre / raporlanan değer): yeni birim yerelde güncellenir */
  writeUnitChanged: (to: string) =>
    `Göstergenin birimi ${to} olarak değişti — değer kaydedilmedi; ${to} cinsinden yeniden girin`,
  /** DR3 — ForkSetModal "Birim tercihleri" adımı */
  fork: {
    steps: { indicators: "Göstergeler", units: "Birim tercihleri", create: "Oluştur" },
    stepsLabel: "Veri seti kurulum adımları",
    next: "Devam: Birim tercihleri",
    back: "Geri",
    searchLabel: "Kategori veya gösterge ara",
    intro:
      "Aile seçimi o ailedeki tüm göstergelere uygulanır; tek tek değiştirmek için gösterge listesini açın.",
    loading: "Birim aileleri hazırlanıyor…",
    clientUnitsFailed:
      "Müşterinin mevcut birimleri okunamadı — kayıtlı birimler kurulumda yine korunur.",
    currencyFamily: "Para",
    familySelect: (label: string) => `${label} birimi`,
    applies: (n: number, m: number) =>
      `${n.toString()} göstergeye uygulanacak · ${m.toString()} istisna`,
    metricsToggle: (n: number) => `Göstergeler (${n.toString()})`,
    exceptionTag: "istisna",
    /** TODO-KPI-97: kullanımda olan göstergeye farklı birim seçildi — Oluştur'da reddedilirdi */
    inUseWarn: "Bu göstergede değer ya da hedef var — farklı birim kurulumda kabul edilmez",
    clientUnitBadge: (u: string) => `müşteri birimi ${u} kullanılıyor`,
    metricUnit: (label: string) => `${label} birimi`,
  },
  /** Katalog "Alternatif birimler" çip alanı (UnitVariantsField) + birim combobox'ı (UnitCombobox) */
  variantsField: {
    label: "Başka hangi birimlerle girilebilir?",
    placeholder: "Birim seçin ya da yazın",
    help: "Müşteri bu birimlerden birini seçebilir; değerler otomatik çevrilir",
    add: "Ekle",
    unknownList: (units: string) =>
      `Tanınmayan birim: ${units} — listeden seçin ya da eş anlamlısını yazın`,
    sameAsDefault: "varsayılan birimle aynı — eklenmedi",
    duplicate: "zaten listede — eklenmedi",
    limit: (max: number) => `en çok ${max.toString()} alternatif birim`,
    remove: (u: string) => `${u} birimini kaldır`,
    /** alternatif satırında katsayı: "1 GJ = 277,8 kWh" */
    relation: (def: string, factor: string, alt: string) => `1 ${def} = ${factor} ${alt}`,
    noConversion: "çevrilmez — yalnız etiket değişir",
  },
  /** UnitCombobox (yazarak süz + aile başlıklı liste + Diğer) */
  combobox: {
    listLabel: "Birimler",
    noMatch: "Eşleşen birim yok",
    other: (typed: string) => `Diğer birim: “${typed}”`,
    sameFamily: (label: string) => `${label} — varsayılanla aynı aile`,
    currencyGroup: "Para birimi",
  },
} as const;
